import React, { useState, useEffect } from "react";
import { Row, Col, Card, Modal, Button, Spinner, Form, Tab, Nav, Alert } from "react-bootstrap";
import { Information } from "iconsax-react";
import { useNavigate } from 'react-router-dom';
import MyApiClient from "../../../../../api-client/MyApiClient";
import Select from "react-select";
import { sidSelectStyles } from "../../../../components/Util/sidSelectStyles";
import { sports, tipologiaStruttura } from "../../../../components/Util/enum";
import { useProfile } from "../../../../../hooks/useProfile";
import { conMotivo } from "../../../../components/Util/erroreServer";
import { VAT_OPTIONS, findVatOption, vatFieldsFromOption } from "../../../../components/Util/vatOptions";
import { numeroDaTesto } from "../../../../components/Util/prezzi";

/*
 * Nuovo impianto su una pagina sola, con lo stesso aspetto della scheda impianto (edit.js): un box a
 * sezioni (Informazioni, Prezzi, Forme di pagamento). Prima era un percorso a cinque passi.
 * La disponibilita' si imposta su un impianto gia' salvato: dopo il salvataggio si apre la scheda
 * dell'impianto sulla tab Disponibilita'.
 * "Salva bozza" crea l'impianto Non attivo (non visibile ai clienti); "Salva e attiva" lo crea Attivo.
 */
export const FacilitiesNew = () => {

    const navigate = useNavigate();
    const { profile: profileData } = useProfile({
        autoFetch: true
    });
    const sdiEnabledNoVat = profileData?.club?.invoiceDataSender?.invoice_enabled === true && !profileData?.club?.invoiceDataSender?.vat_number;
    const billingNotConfirmed = profileData && profileData?.club?.invoiceDataSender?.status !== 'CONFIRMED';
    // Stessa regola degli altri prodotti: senza fatturazione confermata, o con SDI
    // attivo ma senza partita IVA, prezzo standard e aliquota non si toccano.
    const priceFieldsLocked = sdiEnabledNoVat || billingNotConfirmed;

    const [loadingShowed, setLoadingShowed] = useState(false);
    const [modalShowed, setModalShowed] = useState(false);
    const [modalTitle, setModalTitle] = useState('');
    const [modalText, setModalText] = useState('');
    // Pagina da aprire alla chiusura del messaggio (impianto creato ma prezzi non salvati)
    const [dopoChiusura, setDopoChiusura] = useState(null);

    const [enabledSports, setEnabledSports] = useState([]);
    const [showingSociOptions, setShowingSociOptions] = useState(false);
    const [showingTesseratiOptions, setShowingTesseratiOptions] = useState(false);
    const [paymentMethods, setPaymentMethods] = useState({
        stripe_enabled: false,
        bank_transfer_enabled: false
    });

    const [formData, setFormData] = useState({
        name: '',
        description: '',
        sportType: '',
        multiCourse: false,
        payable_stripe: false,
        payable_bank: false,
        payable_in_club: false,
        standard_price: 0,
        member_price: 0,
        federated_price: 0,
        fitcoin_cashback: 0,
        // 22% e' l'aliquota che la prenotazione impianti applicava gia' prima che il
        // campo esistesse: il default non cambia il comportamento di nessuno.
        vat_rate: 22
    });

    useEffect(() => {
        if (profileData && profileData.club && profileData.club.sports) {
            const clubSports = profileData.club.sports.map(s => s.sportType);
            setEnabledSports(sports.filter(element => clubSports.includes(element.value)));
        }
    }, [profileData]);

    useEffect(() => {
        const tokenDetailsString = sessionStorage.getItem('userDetails');
        if (tokenDetailsString) {
            const tokenDetails = JSON.parse(tokenDetailsString);
            if (tokenDetails.clubNature === tipologiaStruttura.ASD.value) {
                setShowingSociOptions(true);
            }
            if (tokenDetails.clubNature === tipologiaStruttura.ASD.value || tokenDetails.clubNature === tipologiaStruttura.SSD.value) {
                setShowingTesseratiOptions(true);
            }
            setPaymentMethods({
                stripe_enabled: tokenDetails.stripe_enabled || false,
                bank_transfer_enabled: tokenDetails.bank_transfer_enabled || false
            });
        }
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const mostraErrore = (titolo, testo) => {
        setModalTitle(titolo);
        setModalText(testo);
        setModalShowed(true);
    };

    // Per la bozza bastano nome e sport (il server non crea un impianto senza sport).
    const mancaPerBozza = () => {
        if (!formData.name || !formData.name.trim()) return "Inserisci il nome dell'impianto.";
        if (!formData.sportType) return "Scegli lo sport.";
        return null;
    };

    // Per attivarlo, gli stessi controlli del vecchio percorso, tutti insieme: il primo che manca viene detto in chiaro.
    const cosaManca = () => {
        const manca = mancaPerBozza();
        if (manca) return manca;
        const numero = (v) => numeroDaTesto(v);
        if (!priceFieldsLocked && !(numero(formData.standard_price) >= 0)) return "Inserisci il prezzo standard.";
        if (showingTesseratiOptions && !(numero(formData.federated_price) >= 0)) return "Inserisci il prezzo tesserati / soci.";
        if (!(numero(formData.fitcoin_cashback) >= 0)) return "Il cashback Fitcoin deve essere maggiore o uguale a 0.";
        return null;
    };

    const datiImpianto = (attivo) => ({
        name: formData.name.trim(),
        description: formData.description.trim(),
        sportType: formData.sportType,
        multiCourse: formData.multiCourse === true || formData.multiCourse === 'true',
        showToCustomers: attivo,
        payable_stripe: formData.payable_stripe === true || formData.payable_stripe === 'true',
        payable_bank: formData.payable_bank === true || formData.payable_bank === 'true',
        payable_in_club: formData.payable_in_club === true || formData.payable_in_club === 'true',
        standard_price: numeroDaTesto(formData.standard_price) || 0,
        member_price: numeroDaTesto(showingSociOptions ? formData.federated_price : formData.standard_price) || 0,
        federated_price: numeroDaTesto(showingTesseratiOptions ? formData.federated_price : formData.standard_price) || 0,
        fitcoin_cashback: numeroDaTesto(formData.fitcoin_cashback) || 0,
        vat_rate: formData.vat_rate,
        // undefined viene omesso dal JSON: fuori dallo 0% la Natura non
        // deve proprio esserci, o lo SDI scarta la fattura.
        // Vuota ("" come la rimanda il server) vale come assente: il server non accetta "".
        vat_nature: formData.vat_nature || undefined
    });

    // La creazione accetta solo i dati generali: prezzi e forme di pagamento vanno con la modifica,
    // subito dopo. Poi si apre la scheda sulla Disponibilita', che si imposta su un impianto salvato.
    const salva = async (attivo) => {
        const manca = attivo ? cosaManca() : mancaPerBozza();
        if (manca) {
            mostraErrore("Dati mancanti", manca);
            return;
        }
        try {
            const token = JSON.parse(sessionStorage.getItem('userDetails') || '{}').idToken;
            if (!token) return;
            const apiClient = new MyApiClient();
            const dati = datiImpianto(attivo);

            setLoadingShowed(true);
            const creato = await apiClient.createVenue(token, {
                name: dati.name,
                description: dati.description,
                sportType: dati.sportType,
                multiCourse: dati.multiCourse,
                showToCustomers: dati.showToCustomers
            });
            if (creato && creato.error) {
                setLoadingShowed(false);
                mostraErrore("Errore durante l'operazione", creato.error);
                return;
            }
            const venueId = creato?.data?.id;
            if (!venueId) {
                setLoadingShowed(false);
                console.error("createVenue response:", creato);
                mostraErrore("Errore durante l'operazione", "Errore nella creazione dell'impianto.");
                return;
            }

            const aggiornato = await apiClient.updateVenue(token, venueId, dati);
            setLoadingShowed(false);
            if (aggiornato && aggiornato.error) {
                // L'impianto esiste gia': si apre la sua scheda, dove si possono correggere i prezzi
                setDopoChiusura(`/struttura/booking/facilitiesedit/${venueId}`);
                mostraErrore("Prezzi non salvati", `L'impianto è stato creato ma i prezzi non sono stati salvati: ${aggiornato.error}`);
                return;
            }
            navigate(`/struttura/booking/facilitiesedit/${venueId}?tab=disponibilita`);
        } catch (error) {
            setLoadingShowed(false);
            mostraErrore("Errore durante l'operazione", conMotivo("Si è verificato un errore durante il salvataggio."));
        }
    };

    const chiudiMessaggio = () => {
        setModalShowed(false);
        if (dopoChiusura) navigate(dopoChiusura);
    };

    const interruttore = (fieldName, label) => (
        <div className="sid-switch-row">
            <Form.Check
                type="switch"
                id={`switch-${fieldName}`}
                label={label}
                checked={!!formData[fieldName]}
                onChange={(e) => setFormData(prev => ({ ...prev, [fieldName]: e.target.checked }))}
            />
        </div>
    );

    return (
        <>
            <Modal className="fade" show={loadingShowed} centered>
                <Modal.Body className="text-center">
                    <Spinner animation="border" role="status">
                        <span className="sr-only">Loading...</span>
                    </Spinner>
                    <div>Salvataggio in corso...</div>
                </Modal.Body>
            </Modal>

            <Modal className="fade" show={modalShowed} centered>
                <Modal.Header>
                    <Modal.Title>{modalTitle}</Modal.Title>
                    <Button onClick={chiudiMessaggio} variant="" className="btn-close" />
                </Modal.Header>
                <Modal.Body>
                    <Row><Col>{modalText}</Col></Row>
                </Modal.Body>
                <Modal.Footer className="justify-content-center">
                    <Button className="btn btn-primary btn-add" onClick={chiudiMessaggio}>Chiudi</Button>
                </Modal.Footer>
            </Modal>

            {billingNotConfirmed && (
                <Alert variant="warning" className="d-flex align-items-start gap-2 text-dark" style={{ marginBottom: '1rem' }}>
                    <Information size="20" className="mt-1 flex-shrink-0" />
                    <div>
                        <strong>Metodo di fatturazione non configurato.</strong> Per pubblicare prodotti e renderli acquistabili nell'App SportId Player è necessario configurare e confermare il metodo di fatturazione nella sezione <strong>La mia struttura → Documenti fiscali</strong>. Puoi continuare a creare prodotti e salvarli come bozza.
                    </div>
                </Alert>
            )}

            <div className="custom-tab-1">
                <Tab.Container defaultActiveKey="informazioni">
                    <Nav as="ul" className="nav-tabs" style={{ border: 'none' }}>
                        <Nav.Item as="li" className="tabMenu">
                            <Nav.Link eventKey="informazioni">Informazioni generali</Nav.Link>
                        </Nav.Item>
                    </Nav>
                    <Row className="align-items-start mt-4">
                        <div className="col-12">
                            <Tab.Content>
                                <Tab.Pane eventKey="informazioni">
                                    <Card className="sid-scheda-prodotto">
                                        <Card.Body className="sid-card-body--spaced">
                                            <Row className="g-4">
                                                <Col md={6}>
                                                    <Form.Label className="mb-2">Impianto / Ritrovo *</Form.Label>
                                                    <Form.Control
                                                        className="mt-2"
                                                        type="text"
                                                        name="name"
                                                        value={formData.name}
                                                        onChange={handleChange}
                                                    />
                                                </Col>
                                                <Col md={6}>
                                                    <Form.Label className="mb-2">Sport *</Form.Label>
                                                    <Select
                                                        className="mt-2"
                                                        styles={sidSelectStyles}
                                                        name="sportType"
                                                        value={enabledSports.find(option => option.value === formData.sportType) || null}
                                                        onChange={(selectedOption) => setFormData(prev => ({ ...prev, sportType: selectedOption?.value || '' }))}
                                                        options={enabledSports}
                                                        placeholder=""
                                                    />
                                                </Col>
                                                <Col md={12}>
                                                    <Form.Label className="mb-2">Descrizione</Form.Label>
                                                    <Form.Control
                                                        className="mt-2"
                                                        as="textarea"
                                                        rows={3}
                                                        name="description"
                                                        value={formData.description}
                                                        onChange={handleChange}
                                                    />
                                                </Col>
                                            </Row>
                                            <div className="sid-switch-inline mt-4">
                                                {interruttore("multiCourse", "Multi corso")}
                                            </div>

                                            <h5 className="sid-form-section">Prezzi</h5>
                                            <Row className="g-4">
                                                <Col md={3}>
                                                    <Form.Label className="mb-2">Prezzo standard *</Form.Label>
                                                    <Form.Control
                                                        disabled={priceFieldsLocked}
                                                        className={`mt-2${priceFieldsLocked ? ' sid-ro-input' : ''}`}
                                                        type="number"
                                                        min="0"
                                                        step="0.01"
                                                        name="standard_price"
                                                        value={formData.standard_price}
                                                        onChange={handleChange}
                                                    />
                                                </Col>
                                                <Col md={3}>
                                                    <Form.Label className="mb-2">IVA</Form.Label>
                                                    <Select
                                                        className="mt-2"
                                                        styles={sidSelectStyles}
                                                        isDisabled={priceFieldsLocked}
                                                        name="vat_rate"
                                                        value={findVatOption(formData)}
                                                        onChange={(selectedOption) => setFormData(prev => ({ ...prev, ...vatFieldsFromOption(selectedOption) }))}
                                                        options={VAT_OPTIONS}
                                                    />
                                                </Col>
                                                {showingTesseratiOptions && (
                                                    <Col md={3}>
                                                        <Form.Label className="mb-2">Prezzo tesserati / soci *</Form.Label>
                                                        <Form.Control
                                                            className="mt-2"
                                                            type="number"
                                                            min="0"
                                                            step="0.01"
                                                            name="federated_price"
                                                            value={formData.federated_price}
                                                            onChange={handleChange}
                                                        />
                                                    </Col>
                                                )}
                                                <Col md={3}>
                                                    <Form.Label className="mb-2">Cashback Fitcoin *</Form.Label>
                                                    <Form.Control
                                                        className="mt-2"
                                                        type="number"
                                                        min="0"
                                                        step="0.01"
                                                        name="fitcoin_cashback"
                                                        value={formData.fitcoin_cashback}
                                                        onChange={handleChange}
                                                    />
                                                </Col>
                                            </Row>

                                            <h5 className="sid-form-section">Forme di pagamento</h5>
                                            <div className="sid-switch-inline">
                                                {interruttore("payable_in_club", "Acquisto pagabile in struttura")}
                                                {paymentMethods.stripe_enabled && interruttore("payable_stripe", "Acquisto pagabile su Stripe")}
                                                {paymentMethods.bank_transfer_enabled && interruttore("payable_bank", "Acquisto pagabile con bonifico")}
                                            </div>
                                        </Card.Body>
                                    </Card>

                                    <div className="d-flex justify-content-end gap-4 mt-4">
                                        <button type="button" className="btn btn-dark light" onClick={() => navigate('/struttura/booking/facilities')}>Annulla</button>
                                        <button type="button" className="btn btn-secondary" onClick={() => salva(false)}>Salva bozza</button>
                                        {!billingNotConfirmed && (
                                            <button type="button" className="btn btn-primary" onClick={() => salva(true)}>Salva e attiva</button>
                                        )}
                                    </div>
                                </Tab.Pane>
                            </Tab.Content>
                        </div>
                    </Row>
                </Tab.Container>
            </div>
        </>
    );
};

export default FacilitiesNew;
